import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/shared/api/axios';
import type {
  CreateSamplePayload,
  DispatchSamplePayload,
  FeedbackSamplePayload,
  SampleFilters,
  SampleRequest,
  SampleTrackingSummary,
  TechnicalApprovePayload,
  UpdateSamplePayload,
} from './types';

export const SAMPLING_QUERY_KEYS = {
  all: ['sampling'] as const,
  list: (filters?: SampleFilters) => ['sampling', 'list', filters] as const,
  tracking: (days?: number, mine?: boolean) => ['sampling', 'tracking', days, mine] as const,
  detail: (id: string) => ['sampling', 'detail', id] as const,
};

// ── Query Hooks ─────────────────────────────────────────────────────────────

export function useSampleRequests(filters?: SampleFilters) {
  return useQuery({
    queryKey: SAMPLING_QUERY_KEYS.list(filters),
    queryFn: async () => {
      const p = new URLSearchParams();
      if (filters?.status && filters.status !== 'all') p.set('status', filters.status);
      if (filters?.mine) p.set('mine', 'true');
      if (filters?.dealId) p.set('dealId', filters.dealId);
      if (filters?.leadId) p.set('leadId', filters.leadId);
      if (filters?.contactId) p.set('contactId', filters.contactId);
      if (filters?.limit) p.set('limit', String(filters.limit));
      if (filters?.skip) p.set('skip', String(filters.skip));

      const { data } = await api.get<{ items: SampleRequest[]; total: number }>(`/api/sampling?${p}`);
      return data;
    },
  });
}

export function useSampleTracking(days = 7, mine = false) {
  return useQuery({
    queryKey: SAMPLING_QUERY_KEYS.tracking(days, mine),
    queryFn: async () => {
      const p = new URLSearchParams({ days: String(days) });
      if (mine) p.set('mine', 'true');
      const { data } = await api.get<SampleTrackingSummary>(`/api/sampling/tracking?${p}`);
      return data;
    },
  });
}

export function useSampleRequest(id?: string | null) {
  return useQuery({
    queryKey: SAMPLING_QUERY_KEYS.detail(id || ''),
    queryFn: async () => {
      if (!id) return null;
      const { data } = await api.get<{ sample: SampleRequest }>(`/api/sampling/${id}`);
      return data.sample;
    },
    enabled: Boolean(id),
  });
}

// ── Mutation Hooks ──────────────────────────────────────────────────────────

export function useSampleMutations() {
  const qc = useQueryClient();

  const invalidateAll = () => {
    qc.invalidateQueries({ queryKey: SAMPLING_QUERY_KEYS.all });
  };

  const createSample = useMutation({
    mutationFn: async (payload: CreateSamplePayload) => {
      const { data } = await api.post<{ sample: SampleRequest }>('/api/sampling', payload);
      return data.sample;
    },
    onSuccess: invalidateAll,
  });

  const updateSample = useMutation({
    mutationFn: async ({ id, payload }: { id: string; payload: UpdateSamplePayload }) => {
      const { data } = await api.patch<{ sample: SampleRequest }>(`/api/sampling/${id}`, payload);
      return data.sample;
    },
    onSuccess: invalidateAll,
  });

  // Sales fills this form to move draft → to_dispatch. The physical handoff
  // (to_dispatch → dispatched) is now a bare, data-less click Dispatch-team
  // makes from the Pending Dispatch queue — see markDispatchedMutation in
  // DispatchAndChallansPage.tsx, which posts to /api/sampling/:id/dispatch.
  const dispatchSample = useMutation({
    mutationFn: async ({ id, payload }: { id: string; payload: DispatchSamplePayload }) => {
      const { data } = await api.post<{
        sample: SampleRequest;
        deliveryCode?: string;
        qrSent?: boolean;
        sampleInvoice?: { _id: string; invoiceNumber: string } | null;
      }>(
        `/api/sampling/${id}/to-dispatch`,
        payload,
      );
      // deliveryCode/qrSent (present only for a fleet dispatch — the QR
      // delivery-confirmation code) ride along so the caller can show it if
      // the automatic WhatsApp send failed and staff need to share it by hand.
      //
      // sampleInvoice is the document that travels WITH the goods, raised by
      // the same request. This return is an explicit pick rather than a spread
      // of `data`, so anything the route adds has to be listed here too — it
      // was left out once, and the dispatcher was told the invoice had failed
      // while it sat correctly numbered in the database.
      return {
        ...data.sample,
        deliveryCode: data.deliveryCode,
        qrSent: data.qrSent,
        sampleInvoice: data.sampleInvoice ?? null,
      };
    },
    onSuccess: invalidateAll,
  });

  const redispatchSample = useMutation({
    mutationFn: async ({ id, payload }: { id: string; payload: DispatchSamplePayload }) => {
      const { data } = await api.post<{ sample: SampleRequest }>(`/api/sampling/${id}/redispatch`, payload);
      return data.sample;
    },
    onSuccess: invalidateAll,
  });

  const markDelivered = useMutation({
    mutationFn: async (id: string) => {
      const { data } = await api.post<{ sample: SampleRequest }>(`/api/sampling/${id}/delivered`);
      return data.sample;
    },
    onSuccess: invalidateAll,
  });

  const recordFeedback = useMutation({
    mutationFn: async ({ id, payload }: { id: string; payload: FeedbackSamplePayload }) => {
      const { data } = await api.post<{ sample: SampleRequest }>(`/api/sampling/${id}/feedback`, payload);
      return data.sample;
    },
    onSuccess: invalidateAll,
  });

  const submitTechnicalReview = useMutation({
    mutationFn: async (id: string) => {
      const { data } = await api.post<{ sample: SampleRequest }>(`/api/sampling/${id}/technical-review`);
      return data.sample;
    },
    onSuccess: invalidateAll,
  });

  const approveTechnical = useMutation({
    mutationFn: async ({ id, payload }: { id: string; payload: TechnicalApprovePayload }) => {
      const { data } = await api.post<{ sample: SampleRequest }>(`/api/sampling/${id}/technical-approve`, payload);
      return data.sample;
    },
    onSuccess: invalidateAll,
  });

  const rejectTechnical = useMutation({
    mutationFn: async ({ id, notes }: { id: string; notes: string }) => {
      const { data } = await api.post<{ sample: SampleRequest }>(`/api/sampling/${id}/reject-technical`, { notes });
      return data.sample;
    },
    onSuccess: invalidateAll,
  });

  const convertToQuotation = useMutation({
    mutationFn: async ({ id, quotationId }: { id: string; quotationId?: string }) => {
      const { data } = await api.post<{ sample: SampleRequest; quotationDraft: Record<string, unknown> }>(
        `/api/sampling/${id}/convert`,
        { quotationId },
      );
      return data;
    },
    onSuccess: () => {
      invalidateAll();
      qc.invalidateQueries({ queryKey: ['quotations'] });
    },
  });

  const closeSample = useMutation({
    mutationFn: async ({ id, reason }: { id: string; reason: string }) => {
      const { data } = await api.post<{ sample: SampleRequest }>(`/api/sampling/${id}/close`, { reason });
      return data.sample;
    },
    onSuccess: invalidateAll,
  });

  const cancelSample = useMutation({
    mutationFn: async ({ id, reason }: { id: string; reason?: string }) => {
      const { data } = await api.post<{ sample: SampleRequest }>(`/api/sampling/${id}/cancel`, { reason });
      return data.sample;
    },
    onSuccess: invalidateAll,
  });

  const reopenSample = useMutation({
    mutationFn: async (id: string) => {
      const { data } = await api.post<{ sample: SampleRequest }>(`/api/sampling/${id}/reopen`);
      return data.sample;
    },
    onSuccess: invalidateAll,
  });

  return {
    createSample,
    updateSample,
    dispatchSample,
    redispatchSample,
    markDelivered,
    recordFeedback,
    submitTechnicalReview,
    approveTechnical,
    rejectTechnical,
    convertToQuotation,
    closeSample,
    cancelSample,
    reopenSample,
  };
}
